import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Play, Pause, SkipForward, Gavel, XCircle, Undo2, Square, StopCircle, Power, ListOrdered, RotateCcw, Monitor } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuction } from "@/hooks/useAuction";
import { useAuth } from "@/context/AuthContext";
import { TimerBar, useClubs } from "@/pages/LiveDisplay";
import { PageHeader, StatusPill, PlayerAvatar, TeamBadge, useConfirm } from "@/components/opl/Common";
import { api, errMsg, fmtL } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const BIDDABLE = ["LIVE", "GOING_ONCE", "GOING_TWICE"];

function Btn({ icon: Icon, label, onClick, disabled, tone = "default", testid }) {
  const tones = { default: "border-white/10 hover:border-[#E5B842]/50", gold: "gold-bg border-transparent", red: "border-red-500/50 text-red-300 hover:bg-red-500/10", green: "bg-emerald-600 hover:bg-emerald-500 border-transparent text-white" };
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testid}
      className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl border font-bold text-sm transition-colors disabled:opacity-30 disabled:pointer-events-none ${tones[tone]}`}>
      <Icon className="h-4 w-4" />{label}
    </button>
  );
}

export default function Auctioneer() {
  const clubs = useClubs();
  const [club, setClub] = useState(localStorage.getItem("opl_club") || "");
  useEffect(() => {
    if (clubs?.length && !clubs.find((c) => c.id === club)) setClub(clubs[0].id);
  }, [clubs, club]);
  const choose = (id) => { localStorage.setItem("opl_club", id); setClub(id); };
  return (
    <div>
      <div className="flex gap-2 mb-6" data-testid="auction-club-tabs">
        {(clubs || []).map((c) => (
          <button key={c.id} onClick={() => choose(c.id)} data-testid={`club-tab-${c.name}`}
            className={`px-5 py-2 rounded-full font-bold text-sm border transition-colors ${club === c.id ? "gold-bg border-transparent" : "border-white/10 text-slate-300"}`}>{c.name} auction</button>
        ))}
      </div>
      {club && <Console key={club} club={club} clubName={clubs?.find((c) => c.id === club)?.name} />}
    </div>
  );
}

function Console({ club, clubName }) {
  const { can } = useAuth();
  const { snap, offset, connected } = useAuction({ club });
  const [pf, setPf] = useState(null);
  const [pool, setPool] = useState([]);
  const [pick, setPick] = useState("");
  const [dialog, confirm] = useConfirm();

  const loadPool = () => api.get("/players").then((r) => setPool(r.data.filter((p) => p.club_id === club))).catch(() => {});
  useEffect(() => {
    api.get(`/auction/${club}/preflight`).then((r) => setPf(r.data)).catch(() => {});
    loadPool();
  }, [club]);
  useEffect(() => { loadPool(); }, [snap?.state?.status, snap?.totals?.unsold, snap?.totals?.remaining, snap?.state?.round]);

  const act = async (path, body) => {
    try {
      await api.post(`/auction/${club}/${path}`, body || {});
      return true;
    } catch (e) {
      toast.error(errMsg(e));
      return false;
    }
  };
  const teamBid = async (t) => {
    if (!snap || !BIDDABLE.includes(snap.state.status)) return toast.error("Start bidding first");
    await act("accept-bid", { team_id: t.id, amount: snap.min_next_bid, expected_version: snap.state.version });
  };
  useEffect(() => {
    const onKey = (e) => {
      if (e.repeat || !["INPUT", "TEXTAREA", "SELECT"].every((t) => t !== e.target.tagName) || !snap) return;
      if (document.querySelector('[role="alertdialog"], [role="dialog"]')) return;
      if (e.target.tagName === "BUTTON" && (e.key === "Enter" || e.key === " ")) e.preventDefault();
      const i = parseInt(e.key, 10);
      if (i >= 1 && i <= snap.teams.length) teamBid(snap.teams[i - 1]);
      else if (e.key.toLowerCase() === "n") act("next");
      else if (e.key.toLowerCase() === "b") act("start-bidding");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });
  if (!snap) return null;
  const st = snap.state;
  const s = st.status;
  const live = BIDDABLE.includes(s);
  const hammer = live || s === "CLOSED" || (s === "PAUSED" && st.player_id);
  const sessionOn = !["IDLE", "SESSION_COMPLETE"].includes(s);
  const canNominate = sessionOn && (!st.player_id || ["SOLD", "UNSOLD"].includes(s) || s === "READY") && !live && s !== "CLOSED";
  const upcoming = pool.filter((p) => p.auction_status === "upcoming" && p.registration_status === "approved");
  const reopenable = pool.filter((p) => ["unsold", "withdrawn"].includes(p.auction_status));
  const leader = snap.teams.find((t) => t.id === st.leading_team_id);

  return (
    <div data-testid="auctioneer-console">
      {dialog}
      <PageHeader title={`${clubName || ""} Live Auction`} crumbs={["Live Auction", clubName]} eyebrow="Auctioneer console">
        <Link to={`/live/${club}`} target="_blank" className="flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 text-sm" data-testid="open-led-btn"><Monitor className="h-4 w-4" />Open LED display</Link>
      </PageHeader>

      {!sessionOn && (
        <div className="surface-2 rounded-2xl p-6 mb-6" data-testid="preflight-panel">
          <div className="flex justify-between items-center flex-wrap gap-3">
            <div><p className="eyebrow">Preflight checklist</p><h3 className="text-xl font-bold mt-1">{pf?.ok ? "Ready to start" : "Setup incomplete"}</h3></div>
            <Btn icon={Power} label="Start auction session" tone="gold" testid="start-session-btn" disabled={!pf?.ok}
              onClick={async () => { if (await act("session/start")) toast.success("Session started"); }} />
          </div>
          <div className="grid md:grid-cols-2 gap-2 mt-4">
            {pf?.checks.map((c) => (
              <div key={c.key} className="flex justify-between gap-3 text-sm px-3 py-2 rounded-lg bg-black/20 min-w-0" data-testid={`preflight-${c.key}`}>
                <span className={c.ok ? "text-emerald-300" : c.blocking ? "text-red-300" : "text-orange-300"}>{c.ok ? "✓" : c.blocking ? "✕" : "!"} {c.label}</span>
                <span className="text-slate-400 text-xs truncate min-w-0 max-w-[45%]">{c.detail}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid xl:grid-cols-3 gap-5">
        <div className="xl:col-span-2 space-y-5">
          <div className="surface-2 rounded-2xl p-6">
            <div className="flex justify-between items-center">
              <StatusPill status={s} big />
              <span className={`text-xs ${connected ? "text-emerald-400" : "text-red-400"}`}>{connected ? "● Synced" : "● Reconnecting"} · v{st.version}</span>
            </div>
            <div className="grid md:grid-cols-2 gap-6 mt-6 items-center">
              <div className="flex gap-4 items-center">
                <PlayerAvatar player={snap.player} size={96} />
                <div>
                  <h2 className="text-2xl font-black" data-testid="console-player-name">{snap.player?.name || "No player nominated"}</h2>
                  {snap.player && <p className="text-sm text-slate-400">{snap.player.role} · {snap.player.category_name} · Base {fmtL(snap.player.effective_base_price)}</p>}
                </div>
              </div>
              <div>
                <p className="eyebrow">Current bid</p>
                <p className="num text-5xl font-black gold-text" data-testid="console-current-bid">{st.current_bid != null ? fmtL(st.current_bid) : "—"}</p>
                <p className="mt-1 text-sm">{leader ? <b>{leader.name}</b> : <span className="text-slate-500">No bids</span>} {snap.min_next_bid != null && BIDDABLE.includes(s) && <span className="text-slate-400">· next min {fmtL(snap.min_next_bid)}</span>}</p>
                <div className="mt-3"><TimerBar snap={snap} offset={offset} /></div>
              </div>
            </div>
          </div>

          <div className="surface rounded-2xl p-5">
            <p className="eyebrow mb-4">Hammer controls</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Btn icon={ListOrdered} label="Next player" onClick={() => act("next")} disabled={!canNominate || (s === "READY" && st.player_id)} testid="next-player-btn" />
              <Btn icon={Play} label="Start bidding" tone="gold" onClick={() => act("start-bidding")} disabled={!(s === "READY" && st.player_id)} testid="start-bidding-btn" />
              {s === "PAUSED" ? <Btn icon={Play} label="Resume" onClick={() => act("resume")} testid="resume-btn" /> :
                <Btn icon={Pause} label="Pause" onClick={() => confirm({ title: "Pause auction", description: "Optionally add a reason shown on the display.", confirmLabel: "Pause", onConfirm: (r) => act("pause", { reason: r }), testid: "pause-dialog" })} disabled={!sessionOn} testid="pause-btn" />}
              <Btn icon={Square} label="Close bidding" onClick={() => act("close")} disabled={!live} testid="close-bidding-btn" />
              <Btn icon={Gavel} label="Mark SOLD" tone="green" disabled={!hammer || !leader} testid="hammer-sold-btn"
                onClick={() => confirm({ title: `Sell ${snap.player?.name}?`, description: `${leader?.name} for ${fmtL(st.current_bid)}. This deducts the purse once and adds the player to the squad.`, confirmLabel: "Confirm SOLD", onConfirm: () => act("sold", { expected_version: st.version }) })} />
              <Btn icon={XCircle} label="Mark UNSOLD" tone="red" disabled={!(hammer || (s === "READY" && st.player_id))} testid="mark-unsold-btn"
                onClick={() => confirm({ title: `Mark ${snap.player?.name} unsold?`, description: "No purse is deducted. The unsold procedure from configuration applies.", confirmLabel: "Mark unsold", destructive: true, onConfirm: () => act("unsold", { expected_version: st.version }) })} />
              <Btn icon={SkipForward} label="Skip player" onClick={() => act("skip", { reason: "skipped" })} disabled={!st.player_id || st.current_bid != null || ["SOLD", "UNSOLD"].includes(s)} testid="skip-player-btn" />
              {can("undo_bid") && <Btn icon={Undo2} label="Undo last bid" disabled={!hammer || st.current_bid == null} testid="undo-bid-btn"
                onClick={() => confirm({ title: "Undo last bid", description: "The last accepted bid will be voided and logged.", requireReason: true, destructive: true, confirmLabel: "Undo bid", onConfirm: (r) => act("undo", { reason: r }) })} />}
              <Btn icon={StopCircle} label="End session" tone="red" disabled={!sessionOn || live || s === "CLOSED"} testid="end-session-btn"
                onClick={() => confirm({ title: "End auction session?", description: "Configuration will unlock. You can start a new session later.", destructive: true, confirmLabel: "End session", onConfirm: () => act("session/end") })} />
              <Btn icon={RotateCcw} label="Re-auction unsold" disabled={!sessionOn || live || s === "CLOSED" || !reopenable.length} testid="reauction-unsold-btn"
                onClick={() => confirm({ title: `Re-auction ${reopenable.length} unsold players?`, description: "They return to the pool for the next round.", confirmLabel: "Re-auction", onConfirm: () => act("reauction-unsold") })} />
            </div>
          </div>

          <div className="surface-2 rounded-2xl p-5" data-testid="team-bid-buttons">
            <div className="flex justify-between items-center mb-3">
              <p className="eyebrow">Team bid buttons</p>
              <span className="text-xs text-slate-400">Keys: 1–{snap.teams.length} bid · N next player · B start bidding</span>
            </div>
            <p className="text-sm text-slate-400 mb-3">Next bid: <b className="num text-[#F5D061]">{snap.min_next_bid != null && BIDDABLE.includes(s) ? fmtL(snap.min_next_bid) : "—"}</b></p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {snap.teams.map((t, i) => {
                const lead = t.id === st.leading_team_id;
                const blocked = t.blocked?.length || (snap.min_next_bid != null && snap.min_next_bid > t.max_bid);
                return (
                  <button key={t.id} onClick={() => teamBid(t)} disabled={!live || lead || !!blocked} data-testid={`team-bid-btn-${t.code}`}
                    title={t.blocked?.[0] || (blocked ? `Max bid ${fmtL(t.max_bid)}` : "")}
                    className={`relative text-left rounded-xl p-3 border transition-colors active:scale-[0.98] disabled:opacity-35 ${lead ? "border-[#E5B842] bg-[#E5B842]/15" : "border-white/10 hover:border-[#E5B842]/60 bg-white/[0.02]"}`}>
                    <span className="absolute top-2 right-2 num text-xs bg-black/40 rounded px-1.5">{i + 1}</span>
                    <div className="flex items-center gap-2"><TeamBadge team={t} size={30} /><span className="font-bold text-sm truncate">{t.name}</span></div>
                    <div className="text-[11px] text-slate-400 mt-2">{fmtL(t.remaining_purse)} · {t.players_count} players{lead ? " · LEADING" : ""}</div>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="surface rounded-2xl p-5 text-sm" data-testid="overlay-links">
            <p className="eyebrow mb-2">Live-stream overlays (OBS browser source)</p>
            {[["player", "Player card"], ["teams", "Purse board"], ["ticker", "Sold ticker"]].map(([k, l]) => (
              <div key={k} className="flex justify-between py-1"><span>{l}</span><a href={`/overlay/${club}/${k}`} target="_blank" rel="noreferrer" className="text-[#E5B842] num text-xs" data-testid={`overlay-link-${k}`}>{window.location.origin}/overlay/{club.slice(0, 6)}…/{k}</a></div>
            ))}
          </div>
        </div>

        <div className="space-y-5">
          <div className="surface rounded-2xl p-5">
            <p className="eyebrow mb-3">Nominate specific player</p>
            <Select value={pick} onValueChange={setPick}>
              <SelectTrigger data-testid="nominate-player-select"><SelectValue placeholder={`${upcoming.length} in pool`} /></SelectTrigger>
              <SelectContent>{upcoming.map((p) => <SelectItem key={p.id} value={p.id}>{p.code} · {p.name}</SelectItem>)}</SelectContent>
            </Select>
            <div className="mt-3"><Btn icon={ListOrdered} label="Nominate" disabled={!canNominate || !pick} onClick={async () => { if (await act("nominate", { player_id: pick })) setPick(""); }} testid="nominate-btn" /></div>
          </div>
          <div className="surface rounded-2xl p-5">
            <p className="eyebrow mb-3">Live bids</p>
            <div className="space-y-1 max-h-64 overflow-y-auto" data-testid="console-bid-list">
              {snap.recent_bids.map((b) => <div key={b.id} className="flex justify-between text-sm px-2 py-1.5 rounded bg-white/[0.02]"><span>{b.team_name} <span className="text-[10px] text-slate-500">{b.source}</span></span><span className="num">{fmtL(b.amount)}</span></div>)}
              {!snap.recent_bids.length && <p className="text-sm text-slate-500">No bids</p>}
            </div>
          </div>
          <div className="surface rounded-2xl p-5">
            <p className="eyebrow mb-3">Teams</p>
            {snap.teams.map((t) => (
              <div key={t.id} className="flex items-center gap-2 py-1.5 text-sm">
                <TeamBadge team={t} size={24} /><span className="flex-1 truncate">{t.name}</span>
                <span className="text-slate-400 text-xs">{t.players_count}p</span><span className="num">{fmtL(t.remaining_purse)}</span>
              </div>
            ))}
          </div>
          {reopenable.length > 0 && (
            <div className="surface rounded-2xl p-5">
              <p className="eyebrow mb-3">Reopen unsold player</p>
              {reopenable.map((p) => (
                <div key={p.id} className="flex justify-between items-center text-sm py-1">
                  <span>{p.name}</span>
                  <button className="text-[#E5B842] flex items-center gap-1 text-xs" data-testid={`reopen-${p.code}`}
                    onClick={() => confirm({ title: `Reopen ${p.name}`, requireReason: true, confirmLabel: "Reopen", onConfirm: (r) => act("reopen", { player_id: p.id, reason: r }) })}>
                    <RotateCcw className="h-3 w-3" />Reopen
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
